import { MutationCache } from '@tanstack/react-query';

import { EventName, EventProps, SegmentAction } from '@/shared/types/segment';

import { toErrorReason } from './errorReason';
import { trackEvent } from './segment';

type MutationCacheConfig = ConstructorParameters<typeof MutationCache>[0];

export type SegmentMutationMeta = {
  /** Emitted when the mutation resolves. */
  success: EventName;
  /** Bounded verb for `Action Failed`, e.g. `campaign_launch`. */
  action: SegmentAction;
  /** Sent on both events. */
  props?: EventProps;
  /**
   * Ids read off the result, for the success event only — a create mutation does
   * not know the new id until it resolves. The `never` parameter is what lets a
   * call site declare the real type: `(view: View) => ({ view_id: view.view_id })`.
   */
  successProps?: (data: never) => EventProps;
};

/**
 * Emits the outcome half of an intent/outcome pair, so no call site writes an
 * `onSuccess` for tracking. Declare it on the mutation:
 *
 * ```ts
 * useMutation({
 *   mutationFn,
 *   meta: { segment: { success: EventName.CampaignLaunched, action: 'campaign_launch' } },
 * })
 * ```
 *
 * First check that no other user action shares the mutation, and that no caller
 * short-circuits its success path — a resolved `{ success: false }` still counts
 * as a success here.
 */
export const trackMutationSuccess: MutationCacheConfig['onSuccess'] = (
  data,
  _variables,
  _context,
  mutation
) => {
  const analytics = mutation.meta?.segment;
  if (analytics) {
    trackEvent(analytics.success, {
      ...analytics.props,
      ...analytics.successProps?.(data as never),
    });
  }
};

export const trackMutationFailure: MutationCacheConfig['onError'] = (
  error,
  _variables,
  _context,
  mutation
) => {
  const analytics = mutation.meta?.segment;
  if (analytics) {
    trackEvent(EventName.ActionFailed, {
      ...analytics.props,
      action: analytics.action,
      error_reason: toErrorReason(error),
    });
  }
};
